import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Alert from './Alert';
import AssistantWaitIndicator from './AssistantWaitIndicator';
import apiService from '../../services/ApiService';
import publicAccessService from '../../services/PublicAccessService';
import RegionalService from '../../services/RegionalService';
import {
  buildAveChatModelOptions,
  buildRagFlowModelLabel,
  findSupportedAveChatModel,
  getAveChatDefaultModelId,
  normalizeRagFlowModelsResponse
} from '../../utils/ragFlowModels';
import {
  getAssistantProcessingDetail,
  hasAssistantProcessingWarning,
  isRagFlowAssistant
} from '../../utils/aveProcessing';
import { renderLatexInHtml } from '../../utils/messageHtmlRenderer';
import aveLogo from '../../assets/logos/logoave.png';
import 'katex/dist/katex.min.css';
import './ChatModal.css';

const CHAT_FONT_SCALE_STORAGE_KEY = 'ave-chat-font-scale';
const CHAT_FONT_SCALE_MIN = 0.65;
const CHAT_FONT_SCALE_MAX = 1.35;
const CHAT_FONT_SCALE_STEP = 0.1;
const CHAT_CONTEXT_STORAGE_PREFIX = 'ave-chat-thread-context-v1';
const CHAT_CONTEXT_MAX_MESSAGES = 40;
const CHAT_CONTEXT_MAX_CHARS = 12000;
const CHAT_CONTEXT_MAX_MESSAGE_CHARS = 1800;

const clampChatFontScale = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 1;
  }

  return Math.min(CHAT_FONT_SCALE_MAX, Math.max(CHAT_FONT_SCALE_MIN, numericValue));
};

const readStoredChatFontScale = () => {
  if (typeof window === 'undefined') {
    return 1;
  }

  return clampChatFontScale(window.localStorage.getItem(CHAT_FONT_SCALE_STORAGE_KEY));
};

const AveLogoMark = ({ className = '' }) => (
  <img
    src={aveLogo}
    alt="AVE"
    className={`ave-logo-mark${className ? ` ${className}` : ''}`}
  />
);

const resolveAssistantId = (assistant) =>
  assistant?.idOpenAIAssistant ||
  assistant?.IdOpenAIAssistant ||
  assistant?.idAssistant ||
  assistant?.IdAssistant ||
  assistant?.idRagFlowChat ||
  assistant?.IdRagFlowChat ||
  assistant?.id ||
  assistant?.Id;

const resolveAssistantName = (assistant, headerTitle) =>
  headerTitle ||
  assistant?.nombreAsistente ||
  assistant?.NombreAsistente ||
  assistant?.nombre ||
  assistant?.Nombre ||
  assistant?.displayName ||
  assistant?.DisplayName ||
  assistant?.email ||
  assistant?.Email ||
  'Asistente';

const isFeatureFlagActive = (flags, flagName) => {
  const normalizedTarget = flagName.toLowerCase();
  return flags.some((flag) => {
    const nombre = (flag?.nombre || flag?.Nombre || '').toLowerCase();
    const activo = flag?.activo ?? flag?.Activo;
    return nombre === normalizedTarget && Boolean(activo);
  });
};

const getSpeechRecognitionConstructor = () => {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const canUseSpeechRecognition = () =>
  typeof navigator !== 'undefined' &&
  Boolean(navigator.mediaDevices?.getUserMedia) &&
  Boolean(getSpeechRecognitionConstructor());

const canUseSpeechSynthesis = () =>
  typeof window !== 'undefined' &&
  typeof window.speechSynthesis !== 'undefined' &&
  typeof window.SpeechSynthesisUtterance !== 'undefined';

const assistantUsesRagFlow = (assistant) =>
  isRagFlowAssistant(assistant) ||
  Boolean(assistant?.idRagFlowChat || assistant?.IdRagFlowChat || assistant?.idRagFlowDataset || assistant?.IdRagFlowDataset);

const resolveAssistantRagFlowModel = (assistant) =>
  assistant?.ragFlowModelo ||
  assistant?.RagFlowModelo ||
  assistant?.modelo ||
  assistant?.Modelo ||
  '';

const resolveAssistantRegionalId = (assistant) =>
  assistant?.idRegional ||
  assistant?.IdRegional ||
  assistant?.regionalId ||
  assistant?.RegionalId ||
  '';

const speechBlockTags = new Set([
  'ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'DIV', 'DL', 'FIELDSET',
  'FIGCAPTION', 'FIGURE', 'FOOTER', 'FORM', 'H1', 'H2', 'H3', 'H4', 'H5',
  'H6', 'HEADER', 'HR', 'LI', 'MAIN', 'NAV', 'OL', 'P', 'PRE', 'SECTION',
  'TABLE', 'TR', 'UL'
]);

const removeSourceReferencesFromSpeech = (text) =>
  String(text || '')
    .replace(/\r/g, '\n')
    .replace(/\s*fuentes?\s*:[^\n]*(?=\n|$)/gi, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{2,}/g, '\n')
    .trim();

const htmlToPlainText = (html) => {
  if (!html) return '';
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return removeSourceReferencesFromSpeech(String(html).replace(/<[^>]+>/g, '\n')).replace(/\s+/g, ' ').trim();
  }

  const parsedDocument = new window.DOMParser().parseFromString(String(html), 'text/html');
  parsedDocument.querySelectorAll('script, style, audio, video').forEach((node) => node.remove());
  parsedDocument.querySelectorAll('p, li, span').forEach((node) => {
    if (/^\s*fuentes?\s*:/i.test(node.textContent || '')) {
      node.remove();
    }
  });

  const collectText = (node) => {
    if (!node) return '';
    if (node.nodeType === window.Node.TEXT_NODE) {
      return node.textContent || '';
    }
    if (node.nodeType !== window.Node.ELEMENT_NODE && node.nodeType !== window.Node.DOCUMENT_NODE) {
      return '';
    }

    const tagName = node.tagName || '';
    if (tagName === 'BR') {
      return '\n';
    }

    const text = Array.from(node.childNodes).map(collectText).join('');
    return speechBlockTags.has(tagName) ? `\n${text}\n` : text;
  };

  return removeSourceReferencesFromSpeech(collectText(parsedDocument.body)).replace(/\s+/g, ' ').trim();
};

const htmlToCopyText = (html) => {
  if (!html) return '';
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return String(html).replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').trim();
  }

  const parsedDocument = new window.DOMParser().parseFromString(String(html), 'text/html');
  parsedDocument.querySelectorAll('script, style, audio, video').forEach((node) => node.remove());

  const collectText = (node) => {
    if (!node) return '';
    if (node.nodeType === window.Node.TEXT_NODE) {
      return node.textContent || '';
    }
    if (node.nodeType !== window.Node.ELEMENT_NODE && node.nodeType !== window.Node.DOCUMENT_NODE) {
      return '';
    }

    const tagName = node.tagName || '';
    if (tagName === 'BR') {
      return '\n';
    }

    const text = Array.from(node.childNodes).map(collectText).join('');
    return speechBlockTags.has(tagName) ? `\n${text}\n` : text;
  };

  return collectText(parsedDocument.body)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const buildChatContextStorageKey = (assistantId, threadId) => {
  if (!assistantId || !threadId) return null;
  return `${CHAT_CONTEXT_STORAGE_PREFIX}:${assistantId}:${threadId}`;
};

const normalizeStoredChatMessage = (message) => {
  if (!message || !['user', 'assistant'].includes(message.role)) {
    return null;
  }

  const content = String(message.content || '').trim();
  if (!content) {
    return null;
  }

  return {
    id: message.id || `${message.role}-${Date.now()}`,
    content,
    role: message.role,
    timestamp: message.timestamp ? new Date(message.timestamp) : new Date()
  };
};

const readStoredChatMessages = (assistantId, threadId) => {
  if (typeof window === 'undefined') return [];

  const storageKey = buildChatContextStorageKey(assistantId, threadId);
  if (!storageKey) return [];

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) return [];

    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map(normalizeStoredChatMessage)
      .filter(Boolean)
      .slice(-CHAT_CONTEXT_MAX_MESSAGES);
  } catch (error) {
    console.warn('No se pudo leer el contexto previo del chat:', error);
    return [];
  }
};

const persistChatMessages = (assistantId, threadId, nextMessages) => {
  if (typeof window === 'undefined') return;

  const storageKey = buildChatContextStorageKey(assistantId, threadId);
  if (!storageKey) return;

  try {
    const normalizedMessages = nextMessages
      .map(normalizeStoredChatMessage)
      .filter(Boolean)
      .slice(-CHAT_CONTEXT_MAX_MESSAGES);

    window.localStorage.setItem(storageKey, JSON.stringify(normalizedMessages));
  } catch (error) {
    console.warn('No se pudo guardar el contexto previo del chat:', error);
  }
};

const getContextMessageText = (message) => {
  if (message.role === 'assistant') {
    return htmlToCopyText(message.content);
  }

  return String(message.content || '').trim();
};

const truncateContextText = (text, maxLength = CHAT_CONTEXT_MAX_MESSAGE_CHARS) => {
  const normalized = String(text || '').replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, Math.max(0, maxLength)).trim()} [...]`;
};

const buildPreviousConversationContext = (conversationMessages) => {
  const contextBlocks = conversationMessages
    .filter((message) => ['user', 'assistant'].includes(message.role) && !String(message.id || '').startsWith('error-'))
    .map((message) => {
      const text = truncateContextText(getContextMessageText(message));
      if (!text) return null;

      const role = message.role === 'assistant' ? 'Asistente' : 'Usuario';
      return `${role}: ${text}`;
    })
    .filter(Boolean);

  const selectedBlocks = [];
  let totalChars = 0;

  for (let index = contextBlocks.length - 1; index >= 0; index -= 1) {
    const block = contextBlocks[index];
    const projectedLength = totalChars + block.length + (selectedBlocks.length > 0 ? 2 : 0);
    if (projectedLength > CHAT_CONTEXT_MAX_CHARS && selectedBlocks.length > 0) {
      break;
    }

    if (projectedLength > CHAT_CONTEXT_MAX_CHARS) {
      selectedBlocks.unshift(truncateContextText(block, CHAT_CONTEXT_MAX_CHARS - totalChars));
      break;
    }

    selectedBlocks.unshift(block);
    totalChars = projectedLength;
  }

  return selectedBlocks.join('\n\n').trim();
};

const appendPublicAccessTokenToUrl = (href) => {
  const token = publicAccessService.getToken();
  if (!href || !token) return href;

  try {
    const url = new URL(href, window.location.origin);
    url.searchParams.set('publicAccessToken', token);
    return url.toString();
  } catch (error) {
    const separator = href.includes('?') ? '&' : '?';
    return `${href}${separator}publicAccessToken=${encodeURIComponent(token)}`;
  }
};

const normalizeResponseSeconds = (value) => {
  const numericValue = typeof value === 'string'
    ? Number(value.replace(',', '.'))
    : Number(value);

  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return null;
  }

  return numericValue;
};

const resolveResponseSeconds = (source) =>
  normalizeResponseSeconds(
    source?.tiempoRespuestaSegundos ??
    source?.TiempoRespuestaSegundos ??
    source?.responseTimeSeconds ??
    source?.ResponseTimeSeconds
  );

const resolveResponseModel = (source) =>
  source?.modeloConsulta ??
  source?.ModeloConsulta ??
  source?.modelo ??
  source?.Modelo ??
  source?.model ??
  source?.Model ??
  null;

const ChatModal = ({ isOpen, onClose, assistant, publicChat = false, regionalId, areaId, password, headerTitle }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [showAudioToggle, setShowAudioToggle] = useState(false);
  const [audioReplyEnabled, setAudioReplyEnabled] = useState(false);
  const [showAudioInput, setShowAudioInput] = useState(false);
  const [selectedAveModel, setSelectedAveModel] = useState('');
  const [showAveModelInfo, setShowAveModelInfo] = useState(false);
  const [aveModelCatalog, setAveModelCatalog] = useState([]);
  const [aveModelsLoading, setAveModelsLoading] = useState(false);
  const [aveModelsError, setAveModelsError] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTranscript, setRecordingTranscript] = useState('');
  const [voiceLevel, setVoiceLevel] = useState(0);
  const [isSpeakingAudio, setIsSpeakingAudio] = useState(false);
  const [speakingMessageId, setSpeakingMessageId] = useState(null);
  const [isSpeechPaused, setIsSpeechPaused] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [fontScale, setFontScale] = useState(readStoredChatFontScale);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const overlayRef = useRef(null);
  const overlayPointerStartedOnBackdropRef = useRef(false);
  const modalRef = useRef(null);
  const speechRecognitionRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserFrameRef = useRef(null);
  const finalTranscriptRef = useRef('');
  const interimTranscriptRef = useRef('');
  const discardRecordingRef = useRef(false);
  const audioUtteranceRef = useRef(null);
  const copyFeedbackTimeoutRef = useRef(null);
  const hasDataSourceWarning = hasAssistantProcessingWarning(assistant);
  const dataSourceWarningDetail = getAssistantProcessingDetail(assistant);
  const isAveAssistant = assistantUsesRagFlow(assistant);
  const assistantDefaultModel = resolveAssistantRagFlowModel(assistant);
  const aveModelOptions = useMemo(
    () => buildAveChatModelOptions(aveModelCatalog, assistantDefaultModel),
    [aveModelCatalog, assistantDefaultModel]
  );
  const selectedAveModelConfig = useMemo(
    () => aveModelOptions.find((model) => model.id === selectedAveModel) ||
      findSupportedAveChatModel(aveModelOptions, selectedAveModel) ||
      null,
    [aveModelOptions, selectedAveModel]
  );
  const [isInputFocused, setIsInputFocused] = useState(false);

  const updateChatViewportHeight = useCallback(() => {
    if (typeof window === 'undefined') return;

    const visualViewport = window.visualViewport;
    const viewportHeight = Math.floor(visualViewport?.height || window.innerHeight || 0);
    const viewportWidth = Math.floor(visualViewport?.width || window.innerWidth || 0);
    const viewportTop = Math.max(0, Math.floor(visualViewport?.offsetTop || 0));
    const viewportLeft = Math.max(0, Math.floor(visualViewport?.offsetLeft || 0));
    const elements = [overlayRef.current, modalRef.current].filter(Boolean);

    elements.forEach((element) => {
      if (viewportHeight > 0) {
        element.style.setProperty('--chat-viewport-height', `${viewportHeight}px`);
      }
      if (viewportWidth > 0) {
        element.style.setProperty('--chat-viewport-width', `${viewportWidth}px`);
      }
      element.style.setProperty('--chat-viewport-offset-top', `${viewportTop}px`);
      element.style.setProperty('--chat-viewport-offset-left', `${viewportLeft}px`);
    });
  }, []);

  // Auto scroll al final cuando hay nuevos mensajes
  const scrollToBottom = useCallback((behavior = 'smooth') => {
    const messagesContainer = messagesContainerRef.current;
    if (messagesContainer) {
      messagesContainer.scrollTo({
        top: messagesContainer.scrollHeight,
        behavior
      });
      return;
    }

    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return undefined;

    updateChatViewportHeight();
    const overlayElement = overlayRef.current;
    const modalElement = modalRef.current;
    const visualViewport = window.visualViewport;

    window.addEventListener('resize', updateChatViewportHeight);
    window.addEventListener('orientationchange', updateChatViewportHeight);
    visualViewport?.addEventListener('resize', updateChatViewportHeight);
    visualViewport?.addEventListener('scroll', updateChatViewportHeight);

    return () => {
      window.removeEventListener('resize', updateChatViewportHeight);
      window.removeEventListener('orientationchange', updateChatViewportHeight);
      visualViewport?.removeEventListener('resize', updateChatViewportHeight);
      visualViewport?.removeEventListener('scroll', updateChatViewportHeight);
      overlayElement?.style.removeProperty('--chat-viewport-height');
      overlayElement?.style.removeProperty('--chat-viewport-width');
      overlayElement?.style.removeProperty('--chat-viewport-offset-top');
      overlayElement?.style.removeProperty('--chat-viewport-offset-left');
      modalElement?.style.removeProperty('--chat-viewport-height');
      modalElement?.style.removeProperty('--chat-viewport-width');
      modalElement?.style.removeProperty('--chat-viewport-offset-top');
      modalElement?.style.removeProperty('--chat-viewport-offset-left');
    };
  }, [isOpen, updateChatViewportHeight]);

  const handleInputFocus = () => {
    setIsInputFocused(true);
    updateChatViewportHeight();
    window.setTimeout(() => {
      updateChatViewportHeight();
      scrollToBottom('auto');
    }, 250);
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
    window.setTimeout(updateChatViewportHeight, 120);
  };

  const handleOverlayPointerDown = (event) => {
    overlayPointerStartedOnBackdropRef.current = event.target === event.currentTarget;
  };

  const handleOverlayClick = (event) => {
    const startedOnBackdrop = overlayPointerStartedOnBackdropRef.current;
    overlayPointerStartedOnBackdropRef.current = false;

    if (startedOnBackdrop && event.target === event.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 220)}px`;
  }, [inputMessage, isOpen]);

  const stopMediaStream = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  };

  const stopVoiceMeter = () => {
    if (analyserFrameRef.current) {
      window.cancelAnimationFrame(analyserFrameRef.current);
      analyserFrameRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    setVoiceLevel(0);
  };

  const cleanupRecording = () => {
    discardRecordingRef.current = true;
    const recognition = speechRecognitionRef.current;

    if (recognition) {
      try {
        recognition.abort();
      } catch (error) {
        console.warn('No se pudo detener el reconocimiento de voz limpiamente:', error);
      }
    }

    speechRecognitionRef.current = null;
    stopVoiceMeter();
    stopMediaStream();
    finalTranscriptRef.current = '';
    interimTranscriptRef.current = '';
    setIsRecording(false);
    setRecordingTranscript('');
  };

  // Inicializar chat cuando se abre el modal
  useEffect(() => {
    if (isOpen && assistant) {
      const assistantFlags = assistant?.featureFlags || assistant?.FeatureFlags || [];
      const flags = assistantFlags.length > 0 ? assistantFlags : RegionalService.getFeatureFlags();
      const isAudioReplyEnabled = isFeatureFlagActive(flags, 'PermiteRespuestaAudioChat');
      const isAudioInputEnabled = isFeatureFlagActive(flags, 'PermiteEntradaAudioChat') && canUseSpeechRecognition();
      setShowAudioToggle(isAudioReplyEnabled);
      setShowAudioInput(isAudioInputEnabled);
      if (!isAudioReplyEnabled) {
        setAudioReplyEnabled(false);
      }
      if (isAveAssistant) {
        loadAveChatModels();
      } else {
        setSelectedAveModel('');
        setShowAveModelInfo(false);
        setAveModelCatalog([]);
        setAveModelsError('');
      }
      initializeChat();
    } else {
      // Limpiar estado cuando se cierra
      cleanupRecording();
      setMessages([]);
      setThreadId(null);
      setIsInitializing(true);
      setInputMessage('');
      setAlert({ show: false, message: '', type: 'error' });
      setShowAudioToggle(false);
      setAudioReplyEnabled(false);
      setShowAudioInput(false);
      setSelectedAveModel('');
      setShowAveModelInfo(false);
      setAveModelCatalog([]);
      setAveModelsError('');
      setAveModelsLoading(false);
      setCopiedMessageId(null);
      stopSpokenResponse();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, assistant]);

  useEffect(() => {
    setShowAveModelInfo(false);
  }, [selectedAveModel]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(CHAT_FONT_SCALE_STORAGE_KEY, String(fontScale));
    }
  }, [fontScale]);

  useEffect(() => {
    return () => {
      cleanupRecording();
      if (copyFeedbackTimeoutRef.current) {
        window.clearTimeout(copyFeedbackTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const appendTranscribedText = (text) => {
    const cleanedText = (text || '').trim();
    if (!cleanedText) return;

    setInputMessage((previous) => {
      const trimmedPrevious = previous.trimEnd();
      return trimmedPrevious ? `${trimmedPrevious}\n${cleanedText}` : cleanedText;
    });

    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const focusChatInput = () => {
    setTimeout(() => {
      const input = inputRef.current;
      if (!input || input.disabled) return;
      input.focus();
    }, 0);
  };

  const loadAveChatModels = async () => {
    if (!assistantUsesRagFlow(assistant)) {
      return;
    }

    setAveModelsLoading(true);
    setAveModelsError('');

    try {
      const activeRegionalId = regionalId || resolveAssistantRegionalId(assistant) || RegionalService.getActiveRegionalId();
      const query = activeRegionalId
        ? `${publicChat ? 'regionalId' : 'idRegional'}=${encodeURIComponent(activeRegionalId)}`
        : '';
      const endpoint = publicChat
        ? `/Asistente/publico/ragflow/modelos${query ? `?${query}` : ''}`
        : `/Asistente/ragflow/modelos${query ? `?${query}` : ''}`;
      const response = publicChat
        ? await apiService.getPublic(endpoint)
        : await apiService.get(endpoint);
      const modelos = normalizeRagFlowModelsResponse(response);

      setAveModelCatalog(modelos);
      setSelectedAveModel(getAveChatDefaultModelId(modelos, resolveAssistantRagFlowModel(assistant)));
    } catch (error) {
      console.error('❌ Error al cargar modelos AVE:', error);
      setAveModelCatalog([]);
      setSelectedAveModel('');
      setAveModelsError('No se pudieron cargar los modelos AVE.');
    } finally {
      setAveModelsLoading(false);
    }
  };

  const startVoiceMeter = (stream) => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;

    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    const data = new Uint8Array(analyser.frequencyBinCount);
    audioContextRef.current = audioContext;

    const tick = () => {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let index = 0; index < data.length; index += 1) {
        const value = (data[index] - 128) / 128;
        sum += value * value;
      }

      const rms = Math.sqrt(sum / data.length);
      setVoiceLevel(Math.min(1, rms * 5));
      analyserFrameRef.current = window.requestAnimationFrame(tick);
    };

    tick();
  };

  const finishSpeechRecognition = () => {
    const shouldDiscard = discardRecordingRef.current;
    discardRecordingRef.current = false;

    const finalText = finalTranscriptRef.current.trim();
    const interimText = interimTranscriptRef.current.trim();
    const textToAppend = finalText || interimText;

    speechRecognitionRef.current = null;
    stopVoiceMeter();
    stopMediaStream();
    setIsRecording(false);
    setRecordingTranscript('');
    finalTranscriptRef.current = '';
    interimTranscriptRef.current = '';

    if (!shouldDiscard && textToAppend) {
      appendTranscribedText(textToAppend);
    }
  };

  const stopSpokenResponse = () => {
    if (canUseSpeechSynthesis()) {
      window.speechSynthesis.cancel();
    }
    audioUtteranceRef.current = null;
    setIsSpeakingAudio(false);
    setSpeakingMessageId(null);
    setIsSpeechPaused(false);
  };

  const speakResponseInBrowser = (htmlContent, messageId = null) => {
    if (!canUseSpeechSynthesis()) {
      setAlert({
        show: true,
        message: 'Tu navegador no permite reproducir la respuesta como audio.',
        type: 'error'
      });
      return;
    }

    const text = htmlToPlainText(htmlContent);
    if (!text) return;

    stopSpokenResponse();
    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.lang = 'es-AR';
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.onend = () => {
      if (audioUtteranceRef.current === utterance) {
        audioUtteranceRef.current = null;
        setIsSpeakingAudio(false);
        setSpeakingMessageId(null);
        setIsSpeechPaused(false);
      }
    };
    utterance.onerror = () => {
      if (audioUtteranceRef.current === utterance) {
        audioUtteranceRef.current = null;
      }
      setIsSpeakingAudio(false);
      setSpeakingMessageId(null);
      setIsSpeechPaused(false);
    };

    audioUtteranceRef.current = utterance;
    setIsSpeakingAudio(true);
    setSpeakingMessageId(messageId);
    setIsSpeechPaused(false);
    window.speechSynthesis.speak(utterance);
  };

  const toggleBrowserAudio = (message) => {
    if (!canUseSpeechSynthesis()) {
      setAlert({
        show: true,
        message: 'Tu navegador no permite reproducir la respuesta como audio.',
        type: 'error'
      });
      return;
    }

    const isCurrentMessage = speakingMessageId === message.id && isSpeakingAudio;
    if (isCurrentMessage) {
      if (isSpeechPaused || window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
        setIsSpeechPaused(false);
      } else {
        window.speechSynthesis.pause();
        setIsSpeechPaused(true);
      }
      return;
    }

    speakResponseInBrowser(message.audio?.text || message.content, message.id);
  };

  const startRecording = async () => {
    const SpeechRecognition = getSpeechRecognitionConstructor();
    if (!canUseSpeechRecognition() || !SpeechRecognition) {
      setAlert({
        show: true,
        message: 'Tu navegador no permite dictado por voz desde esta pantalla.',
        type: 'error'
      });
      return;
    }

    try {
      setAlert({ show: false, message: '', type: 'error' });
      discardRecordingRef.current = false;
      finalTranscriptRef.current = '';
      interimTranscriptRef.current = '';
      setRecordingTranscript('');

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      startVoiceMeter(stream);

      const recognition = new SpeechRecognition();
      recognition.lang = 'es-AR';
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.onresult = (event) => {
        let interimText = '';
        for (let index = event.resultIndex; index < event.results.length; index += 1) {
          const transcript = event.results[index][0]?.transcript || '';
          if (event.results[index].isFinal) {
            finalTranscriptRef.current = `${finalTranscriptRef.current} ${transcript}`.trim();
          } else {
            interimText += transcript;
          }
        }

        interimTranscriptRef.current = interimText.trim();
        setRecordingTranscript(`${finalTranscriptRef.current} ${interimText}`.trim());
      };

      recognition.onerror = (event) => {
        if (!['aborted', 'no-speech'].includes(event.error)) {
          setAlert({
            show: true,
            message: 'No se pudo interpretar el audio. Probá nuevamente.',
            type: 'error'
          });
        }
      };

      recognition.onend = finishSpeechRecognition;

      speechRecognitionRef.current = recognition;
      recognition.start();
      setIsRecording(true);
    } catch (error) {
      console.error('❌ Error al iniciar grabación:', error);
      cleanupRecording();
      setAlert({
        show: true,
        message: 'No se pudo acceder al micrófono.',
        type: 'error'
      });
    }
  };

  const stopRecording = () => {
    const recognition = speechRecognitionRef.current;
    if (!recognition) {
      finishSpeechRecognition();
      return;
    }

    discardRecordingRef.current = false;
    recognition.stop();
  };

  const handleRecordButtonClick = () => {
    if (isRecording) {
      stopRecording();
      return;
    }

    startRecording();
  };

  const initializeChat = async () => {
    try {
      setIsInitializing(true);
      setAlert({ show: false, message: '', type: 'error' });
      
      const assistantId = resolveAssistantId(assistant);
      if (!assistantId) {
        throw new Error('No se encontró el asistente seleccionado');
      }

      if (publicChat && !regionalId) {
        throw new Error('Falta el regional para iniciar la conversación');
      }
      if (publicChat && !areaId) {
        throw new Error('Falta el área para iniciar la conversación');
      }

      if (publicChat) {
        const response = await apiService.postPublic('/Asistente/publico/threads', {
          idOpenAIAssistant: assistantId,
          regionalId,
          areaId,
          password
        });

        if (response.ok && response.threadId) {
          setThreadId(response.threadId);
          setMessages(readStoredChatMessages(assistantId, response.threadId));
        } else {
          throw new Error(response.error || 'Error al inicializar la conversación');
        }
      } else {
        const response = await apiService.post('/Threads/threads', {
          idOpenAIAssistant: assistantId
        });

        if (response.ok && response.threadId) {
          setThreadId(response.threadId);
          setMessages(readStoredChatMessages(assistantId, response.threadId));
        } else {
          throw new Error(response.error || 'Error al inicializar la conversación');
        }
      }
      
    } catch (error) {
      console.error('❌ Error al inicializar chat:', error);
      setAlert({
        show: true,
        message: error.message || 'Error al inicializar la conversación',
        type: 'error'
      });
    } finally {
      setIsInitializing(false);
    }
  };

  // Función para extraer HTML del bloque de código markdown
  const extractHtmlFromMarkdown = (content) => {
    
    // Si el contenido viene envuelto en ```html ... ```
    const htmlBlockMatch = content.match(/```html\s*\n([\s\S]*?)\n```/);
    if (htmlBlockMatch) {
      const htmlContent = htmlBlockMatch[1];
      return extractBodyContent(htmlContent);
    }
    
    // Si viene envuelto en ``` ... ``` (sin especificar html)
    const genericBlockMatch = content.match(/```\s*\n([\s\S]*?)\n```/);
    if (genericBlockMatch) {
      const innerContent = genericBlockMatch[1];
      // Verificar si parece HTML
      if (innerContent.includes('<html>') || innerContent.includes('<!DOCTYPE')) {
        return extractBodyContent(innerContent);
      }
    }
    
    // Si no hay bloque de código pero parece HTML completo
    if (content.includes('<!DOCTYPE') || content.includes('<html>')) {
      return extractBodyContent(content);
    }
    
    return content;
  };

  // Función para extraer solo el contenido del body de un HTML completo
  const extractBodyContent = (htmlString) => {
    // Buscar el contenido dentro de las etiquetas <body>
    const bodyMatch = htmlString.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch) {
      return bodyMatch[1].trim();
    }
    
    // Si no hay body, pero hay html, buscar después del head
    const afterHeadMatch = htmlString.match(/<\/head>\s*([\s\S]*?)<\/html>/i);
    if (afterHeadMatch) {
      return afterHeadMatch[1].trim();
    }
    
    // Si no encuentra estructura HTML completa, retornar tal como está
    return htmlString;
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !threadId || isLoading || isRecording) return;

    const assistantId = resolveAssistantId(assistant);
    if (!assistantId) {
      setAlert({
        show: true,
        message: 'No se encontró el asistente seleccionado',
        type: 'error'
      });
      return;
    }

    const messageToSend = inputMessage.trim();
    const previousContext = buildPreviousConversationContext(messages);
    const userMessage = {
      id: 'user-' + Date.now(),
      content: messageToSend,
      role: 'user',
      timestamp: new Date()
    };

    const shouldUseBrowserAudioReply = audioReplyEnabled && isAveAssistant;
    const shouldRequestServerAudio = audioReplyEnabled && !isAveAssistant;
    const messagesWithUser = [...messages, userMessage];
    
    // Agregar mensaje del usuario inmediatamente
    stopSpokenResponse();
    persistChatMessages(assistantId, threadId, messagesWithUser);
    setMessages(messagesWithUser);
    setInputMessage('');
    setIsLoading(true);

    try {
      
      // Llamada real a la API
      if (publicChat && !regionalId) {
        throw new Error('Falta el regional para enviar el mensaje');
      }
      if (publicChat && !areaId) {
        throw new Error('Falta el área para enviar el mensaje');
      }

      const response = publicChat
        ? await apiService.postPublic('/Asistente/publico/chat', {
            idAsistente: assistantId,
            idThread: threadId,
            mensaje: messageToSend,
            regionalId,
            areaId,
            password,
            permiteRespuestaAudioChat: shouldRequestServerAudio,
            ragFlowModelo: isAveAssistant ? selectedAveModel : undefined,
            contextoPrevio: previousContext || undefined
          })
        : await apiService.post('/Asistente/chat', {
            idAsistente: assistantId,
            idThread: threadId,
            mensaje: messageToSend,
            permiteRespuestaAudioChat: shouldRequestServerAudio,
            ragFlowModelo: isAveAssistant ? selectedAveModel : undefined,
            contextoPrevio: previousContext || undefined
          });


      if (response.ok && response.respuesta) {
        // Extraer HTML del bloque de código markdown si es necesario
        const htmlContent = extractHtmlFromMarkdown(response.respuesta);
        const archivoIds = response.archivoIds || response.ArchivoIds || [];
        const archivoLinks = response.archivoLinks || response.ArchivoLinks || [];
        const normalizedArchivoLinks = Array.isArray(archivoLinks)
          ? (publicChat ? archivoLinks.map(appendPublicAccessTokenToUrl) : archivoLinks)
          : [];
        const hasServerLinks = normalizedArchivoLinks.length > 0;
        const shouldStripLinks = hasServerLinks || /\/mnt\/data\//i.test(htmlContent);
        const safeHtmlContent = shouldStripLinks
          ? htmlContent.replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, '')
          : htmlContent;
        const downloadLinks = hasServerLinks
          ? `
            <div class="download-box">
              <p><strong>Archivos generados</strong></p>
              <ul>
                ${normalizedArchivoLinks.map((href, idx) => {
                  return `<li><a href="${href}" target="_blank" rel="noreferrer">Descargar archivo ${idx + 1}</a></li>`;
                }).join('')}
              </ul>
            </div>
          `
          : (Array.isArray(archivoIds) && archivoIds.length > 0
            ? `
              <div class="download-box">
                <p><strong>Archivos generados</strong></p>
                <ul>
                  ${archivoIds.map((fileId, idx) => {
                    const href = publicChat
                      ? appendPublicAccessTokenToUrl(`${apiService.baseURL}/Asistente/publico/archivos/${fileId}/download?regionalId=${regionalId}`)
                      : `${apiService.baseURL}/Asistente/archivos/${fileId}/download`;
                    return `<li><a href="${href}" target="_blank" rel="noreferrer">Descargar archivo ${idx + 1}</a></li>`;
                  }).join('')}
                </ul>
              </div>
            `
            : '');
        const audioBase64 = response.audioBase64 || response.AudioBase64;
        const audioContentType = response.audioContentType || response.AudioContentType || 'audio/mpeg';
        const audioSrc = audioBase64 ? `data:${audioContentType};base64,${audioBase64}` : '';
        const audioExtension = audioContentType.includes('wav')
          ? 'wav'
          : audioContentType.includes('ogg')
            ? 'ogg'
            : 'mp3';
        const audio = audioSrc
          ? {
              mode: 'file',
              src: audioSrc,
              downloadName: `respuesta.${audioExtension}`
            }
          : (shouldUseBrowserAudioReply
            ? {
                mode: 'browser',
                text: safeHtmlContent
              }
            : null);
        const finalContent = `${safeHtmlContent}${downloadLinks}`;
        
        const assistantMessage = {
          id: response.idMensaje || 'assistant-' + Date.now(),
          content: finalContent,
          audio,
          role: 'assistant',
          timestamp: new Date(response.fechaRespuesta || new Date()),
          model: resolveResponseModel(response),
          responseTimeSeconds: resolveResponseSeconds(response)
        };
        
        const finalMessages = [...messagesWithUser, assistantMessage];
        persistChatMessages(assistantId, threadId, finalMessages);
        setMessages(finalMessages);

        if (shouldUseBrowserAudioReply) {
          speakResponseInBrowser(safeHtmlContent, assistantMessage.id);
        }
        
      } else {
        throw new Error(response.error || 'Error al procesar la respuesta del asistente');
      }
      
    } catch (error) {
      console.error('❌ Error al enviar mensaje:', error);
      
      // Mostrar mensaje de error en el chat
      const errorMessage = {
        id: 'error-' + Date.now(),
        content: `<div class="error-message">
          <p><strong>❌ Error al enviar mensaje</strong></p>
          <p>Lo siento, no pude procesar tu mensaje. Por favor, inténtalo nuevamente.</p>
          <p><em>Detalles: ${error.message}</em></p>
        </div>`,
        role: 'assistant',
        timestamp: new Date()
      };
      
      const finalMessages = [...messagesWithUser, errorMessage];
      persistChatMessages(assistantId, threadId, finalMessages);
      setMessages(finalMessages);
      
      // También mostrar alerta
      setAlert({
        show: true,
        message: `Error al enviar mensaje: ${error.message}`,
        type: 'error'
      });
    } finally {
      setIsLoading(false);
      focusChatInput();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getMessageCopyText = (message) => {
    if (message.role === 'assistant') {
      return htmlToCopyText(message.content);
    }

    return String(message.content || '').trim();
  };

  const writeTextToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const copyMessageToClipboard = async (message) => {
    const text = getMessageCopyText(message);
    if (!text) return;

    try {
      await writeTextToClipboard(text);
      setCopiedMessageId(message.id);
      if (copyFeedbackTimeoutRef.current) {
        window.clearTimeout(copyFeedbackTimeoutRef.current);
      }
      copyFeedbackTimeoutRef.current = window.setTimeout(() => {
        setCopiedMessageId(null);
      }, 1600);
    } catch (error) {
      console.error('❌ Error al copiar mensaje:', error);
      setAlert({
        show: true,
        message: 'No se pudo copiar el mensaje al portapapeles.',
        type: 'error'
      });
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatResponseSeconds = (seconds) => {
    return seconds.toLocaleString('es-AR', {
      minimumFractionDigits: seconds < 1 ? 2 : 1,
      maximumFractionDigits: 3
    });
  };

  const formatAssistantHtml = (html) => renderLatexInHtml(html);

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  const decreaseFontScale = () => {
    setFontScale((current) => clampChatFontScale(Number((current - CHAT_FONT_SCALE_STEP).toFixed(2))));
  };

  const increaseFontScale = () => {
    setFontScale((current) => clampChatFontScale(Number((current + CHAT_FONT_SCALE_STEP).toFixed(2))));
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className={`chat-modal-overlay${isInputFocused ? ' input-focused' : ''}`}
      onPointerDown={handleOverlayPointerDown}
      onClick={handleOverlayClick}
    >
      <div
        ref={modalRef}
        className={`chat-modal${publicChat ? ' public-chat' : ''}${isInputFocused ? ' input-focused' : ''}`}
        style={{ '--chat-font-scale': fontScale }}
      >
        {/* Header */}
        <div className="chat-header">
          <div className="chat-header-info">
            <div className="assistant-avatar">
              <AveLogoMark />
            </div>
            <div className="assistant-info">
              <h3 className="assistant-name">
                {resolveAssistantName(assistant, headerTitle)}
              </h3>
              <p className="assistant-status">
                {isInitializing ? 'Inicializando...' : 
                 threadId ? 'Conectado' : 'Sin conexión'}
              </p>
            </div>
          </div>

          <div className="chat-header-actions">
            <div className="chat-zoom-controls" aria-label="Tamaño de letra">
              <button
                type="button"
                className="chat-zoom-btn"
                onClick={decreaseFontScale}
                disabled={fontScale <= CHAT_FONT_SCALE_MIN}
                aria-label="Reducir tamaño de letra"
                title="Reducir tamaño de letra"
              >
                <i className="fas fa-minus"></i>
              </button>
              <span className="chat-zoom-value">{Math.round(fontScale * 100)}%</span>
              <button
                type="button"
                className="chat-zoom-btn"
                onClick={increaseFontScale}
                disabled={fontScale >= CHAT_FONT_SCALE_MAX}
                aria-label="Aumentar tamaño de letra"
                title="Aumentar tamaño de letra"
              >
                <i className="fas fa-plus"></i>
              </button>
            </div>
            <button className="close-btn" onClick={onClose} aria-label="Cerrar chat" title="Cerrar chat">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {/* Alert */}
        {alert.show && (
          <div className="chat-alert">
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          </div>
        )}

        {hasDataSourceWarning && (
          <div className="chat-datasource-warning" title={dataSourceWarningDetail}>
            <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
            <span>
              {dataSourceWarningDetail || 'Este asistente tiene advertencias en uno o mas archivos del datasource.'}
            </span>
          </div>
        )}

        {/* Messages */}
        <div ref={messagesContainerRef} className="chat-messages">
          {isInitializing ? (
            <div className="initializing-message">
              <div className="thinking-content">
                <div className="thinking-avatar">
                  <i className="fas fa-cog fa-spin"></i>
                </div>
                <div className="thinking-text">
                  <span className="thinking-label">
                    Inicializando conversación con {resolveAssistantName(assistant, headerTitle)}...
                  </span>
                  <div className="typing-indicator">
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {messages.length === 0 && !isLoading && (
                <div className="chat-empty-state">
                  <div className="chat-empty-avatar">
                    <AveLogoMark />
                  </div>
                  <h2>{resolveAssistantName(assistant, headerTitle)}</h2>
                  <p>Listo para conversar</p>
                </div>
              )}

              {messages.map((message) => {
                const responseSeconds = resolveResponseSeconds(message);
                const showResponseTime = message.role === 'assistant' && responseSeconds !== null;

                return (
                  <div
                    key={message.id}
                    className={`message ${message.role === 'user' ? 'user-message' : 
                               message.role === 'system' ? 'system-message' : 'assistant-message'}`}
                  >
                    {message.role === 'assistant' && (
                      <div className="message-avatar assistant-message-avatar">
                        <AveLogoMark />
                      </div>
                    )}
                    <div className="message-content">
                      <div className="message-bubble">
                        <div className="message-text">
                          <div
                            className="message-text-body"
                            dangerouslySetInnerHTML={{
                              __html: message.role === 'assistant'
                                ? formatAssistantHtml(message.content)
                                : message.content.replace(/\n/g, '<br>')
                            }}
                          />
                          {showResponseTime && (
                            <div
                              className="message-response-meta"
                              title={message.model ? `Modelo: ${message.model}` : undefined}
                            >
                              <i className="fas fa-stopwatch" aria-hidden="true"></i>
                              <span>Generado en {formatResponseSeconds(responseSeconds)} s</span>
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          className={`message-copy-btn${copiedMessageId === message.id ? ' copied' : ''}`}
                          onClick={() => copyMessageToClipboard(message)}
                          aria-label={copiedMessageId === message.id ? 'Mensaje copiado' : 'Copiar mensaje'}
                          title={copiedMessageId === message.id ? 'Copiado' : 'Copiar mensaje'}
                        >
                          <i className={`fas ${copiedMessageId === message.id ? 'fa-check' : 'fa-copy'}`}></i>
                        </button>
                      </div>
                      {message.role === 'assistant' && message.audio && (
                        <div className="message-audio-controls">
                          {message.audio.mode === 'file' ? (
                            <>
                              <audio controls src={message.audio.src}></audio>
                              <a
                                className="message-audio-action"
                                href={message.audio.src}
                                download={message.audio.downloadName || 'respuesta.mp3'}
                              >
                                <i className="fas fa-download"></i>
                                <span>Descargar</span>
                              </a>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="message-audio-action"
                                onClick={() => toggleBrowserAudio(message)}
                                aria-label={
                                  speakingMessageId === message.id && isSpeakingAudio && !isSpeechPaused
                                    ? 'Pausar audio'
                                    : 'Reproducir audio'
                                }
                              >
                                <i
                                  className={`fas ${
                                    speakingMessageId === message.id && isSpeakingAudio && !isSpeechPaused
                                      ? 'fa-pause'
                                      : 'fa-play'
                                  }`}
                                ></i>
                                <span>
                                  {speakingMessageId === message.id && isSpeakingAudio && !isSpeechPaused
                                    ? 'Pausar'
                                  : 'Reproducir'}
                                </span>
                              </button>
                            </>
                          )}
                        </div>
                      )}
                      {message.timestamp && (
                        <div className="message-time">
                          {formatTime(message.timestamp)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {isLoading && (
                <div className="message assistant-message thinking-message">
                  <div className="message-content">
                    <AssistantWaitIndicator assistantName={resolveAssistantName(assistant, headerTitle)} />
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="chat-input-area">
          {(showAudioToggle || isAveAssistant) && (
            <div className="chat-options">
              {isAveAssistant && (
                <div className="ave-model-options">
                  <label className="ave-model-field">
                    <span>Modelo AVE</span>
                    <select
                      value={selectedAveModel}
                      onChange={(e) => setSelectedAveModel(e.target.value)}
                      disabled={isLoading || isInitializing || aveModelsLoading || aveModelOptions.length === 0}
                    >
                      {aveModelsLoading && (
                        <option value="">Cargando modelos...</option>
                      )}
                      {!aveModelsLoading && aveModelOptions.length === 0 && (
                        <option value="">Sin modelos activos</option>
                      )}
                      {!aveModelsLoading && aveModelOptions.map((model) => (
                        <option key={model.id} value={model.id}>
                          {buildRagFlowModelLabel(model)}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className={`ave-model-info-btn${showAveModelInfo ? ' active' : ''}`}
                      onClick={() => setShowAveModelInfo((current) => !current)}
                      disabled={!selectedAveModelConfig}
                      aria-label="Ver características del modelo"
                      title="Ver características del modelo"
                    >
                      <i className="fas fa-info"></i>
                    </button>
                  </label>
                  {aveModelsError && (
                    <div className="ave-model-error">
                      {aveModelsError}
                    </div>
                  )}
                  {showAveModelInfo && selectedAveModelConfig && (
                    <div className="ave-model-info-panel">
                      <div className="ave-model-info-title">
                        {buildRagFlowModelLabel(selectedAveModelConfig)}
                      </div>
                      {selectedAveModelConfig.descripcion && (
                        <p className="ave-model-description">
                          {selectedAveModelConfig.descripcion}
                        </p>
                      )}
                      {selectedAveModelConfig.topN && (
                        <p className="ave-model-context">
                          Contexto recuperado: {selectedAveModelConfig.topN} fragmentos relevantes por consulta.
                        </p>
                      )}
                      {Array.isArray(selectedAveModelConfig.caracteristicas) && selectedAveModelConfig.caracteristicas.length > 0 ? (
                        <ul>
                          {selectedAveModelConfig.caracteristicas.map((caracteristica, index) => (
                            <li key={`${selectedAveModelConfig.id || selectedAveModel}-${index}`}>
                              {caracteristica}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p>Sin características adicionales disponibles.</p>
                      )}
                    </div>
                  )}
                </div>
              )}
              {showAudioToggle && (
                <label className="audio-toggle">
                  <input
                    type="checkbox"
                    checked={audioReplyEnabled}
                    onChange={(e) => setAudioReplyEnabled(e.target.checked)}
                  />
                  <span>Respuesta en forma de audio</span>
                </label>
              )}
            </div>
          )}
          {isRecording && (
            <div className="voice-capture-status" role="status" aria-live="polite">
              <div className="voice-meter" aria-hidden="true">
                {[0.35, 0.65, 1, 0.65, 0.35].map((weight, index) => (
                  <span
                    key={weight + index}
                    style={{ height: `${Math.max(7, Math.round(8 + voiceLevel * 28 * weight))}px` }}
                  />
                ))}
              </div>
              <span className="voice-capture-text">
                {recordingTranscript || 'Escuchando...'}
              </span>
            </div>
          )}
          <div className={`chat-input-container${showAudioInput ? ' has-voice-input' : ''}`}>
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder={
                isInitializing
                  ? "Inicializando..."
                  : isRecording
                    ? "Grabando..."
                    : `Mensaje a ${resolveAssistantName(assistant, headerTitle)}`
              }
              disabled={isLoading || isInitializing || !threadId}
              rows={1}
              className="chat-input"
            />
            {showAudioInput && (
              <button
                type="button"
                onClick={handleRecordButtonClick}
                disabled={!isRecording && (isLoading || isInitializing || !threadId)}
                className={`voice-input-btn${isRecording ? ' recording' : ''}`}
                aria-label={isRecording ? 'Detener grabación' : 'Grabar audio'}
                title={isRecording ? 'Detener grabación' : 'Grabar audio'}
              >
                {isRecording ? (
                  <i className="fas fa-stop"></i>
                ) : (
                  <i className="fas fa-microphone"></i>
                )}
              </button>
            )}
            <button
              type="button"
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading || isInitializing || !threadId || isRecording}
              className="send-btn"
            >
              {isLoading ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                <i className="fas fa-paper-plane"></i>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatModal;
